import { useCallback, useMemo } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { intentsEndpoints as endpoints, recommendationsEndpoints } from '@/api/endpoints';
import { ApiError, callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { MODAL_CONFIRM_CHANGES } from '@/components/organisms/Modals/ModalConductor';
import useDialogs from '@/hooks/useDialogs';
import useIntentRecommendation from '@/hooks/useIntentRecommendation';
import { createEntityCache } from '@/hooks/utils/entityCache';
import { getRandomElement } from '@/redux/dialogs/helper';
import { clearIntentExpressions, setIntent } from '@/redux/expressions/actions';
import { selectIntentDraft } from '@/redux/expressions/selectors';
import { popModal, pushModal } from '@/redux/modals/actions';
import { resetDirty, setRecommendations } from '@/redux/recommendations/actions';
import { selectVisibleExpressions } from '@/redux/recommendations/selectors';
import { selectAccountSlug, selectIntentName } from '@/redux/session/selectors';
import { Intents, Intent, PartialIntent } from '@/shared/types/intent';
import { Recommendation } from '@/shared/types/recommendations';
import { RootState } from '@/shared/types/state';
import { MIN_TRAINING_PHRASES, SYSTEM_INTENTS } from '@/util/constants';
import { sameCollection, getMedian } from '@/util/util';

import useDialogNodesCache from './useDialogNodesCache';

interface CollectionProps {
  collection: string;
  new_collection: string;
}
interface UpdateIntentProps {
  name: string;
  intent: PartialIntent | { new_intent: string; expressions: { id: string; text: string }[] };
}

export const API = Object.freeze({
  listIntents: async (brainId: string): Promise<Intents> => callGet(endpoints.intents(brainId)),

  getIntent: async (brainId: string, intentName: string): Promise<Intent> =>
    callGet(endpoints.intent(brainId, intentName)),

  createIntent: async ({
    brainId,
    newIntent,
  }: {
    brainId: string;
    newIntent: Partial<Intent>;
  }): Promise<Intent> => callPost(endpoints.intents(brainId), newIntent),

  updateCollection: async (brainId: string, data: CollectionProps): Promise<Intents> =>
    callPut(endpoints.collection(brainId), data),

  updateIntent: async (brainId: string, { name, intent }: UpdateIntentProps): Promise<Intent> =>
    callPut(endpoints.intent(brainId, name), intent),

  deleteIntent: async ({
    brainId,
    intentName,
  }: {
    brainId: string;
    intentName: string;
  }): Promise<Intent> => callDelete(endpoints.intent(brainId, intentName)),
});

export const intentCache = createEntityCache<Intent, 'intents'>({
  listKey: (brainId) => [endpoints.intents(brainId)],
  detailKey: (brainId, intentName) => [endpoints.intent(brainId, intentName)],
  listField: 'intents',
  getId: (intent) => intent.intent,
  getParentId: (intent) => intent.brain_id,
  afterChange: (queryClient, change) => {
    // A deleted intent can no longer be recommended
    if (change.type === 'removed') {
      queryClient.setQueryData<Recommendation>(
        [recommendationsEndpoints.recommendations(change.parentId)],
        (prev) =>
          prev
            ? { ...prev, intents: prev.intents?.filter((item) => item.intent !== change.id) }
            : undefined
      );
    }
  },
});

export const onIntentCollectionUpdated = (
  queryClient: QueryClient,
  brainId: string,
  oldCollectionName: string,
  newCollectionName: string
) => {
  queryClient.setQueryData<Intents>([endpoints.intents(brainId)], (prev: Intents) => ({
    intents: prev?.intents.map((item) => {
      if (item.collection === oldCollectionName) {
        return { ...item, collection: newCollectionName };
      }
      return item;
    }),
  }));
};

export const OPTIMAL_PHRASES = 10;
export const MIN_UPPER_THRESHOLD = 12;

/**
 * Finds the median number of the number of expressions.
 * Returns the optimal phrases number - its between the median and the maximum phrases allowed
 *If this number is smaller than OPTIMAL_PHRASES, then return OPTIMAL_PHRASES
 */

export const getOptimalPhrases = (intents: Intent[]) => {
  const expressionsArray = intents
    ?.filter((i) => i?.expressions?.length > 0)
    ?.map((i) => i?.expressions?.length);

  const median = getMedian(expressionsArray, MIN_TRAINING_PHRASES);

  const maximumPhrases =
    median + median / 2 < MIN_UPPER_THRESHOLD
      ? MIN_UPPER_THRESHOLD
      : Math.ceil(median + median / 2);

  const optimalPhrases = Math.ceil((maximumPhrases + median) / 2);

  return Math.max(optimalPhrases, OPTIMAL_PHRASES);
};

const useIntents = (brainId: string, intentName?: string) => {
  const navigate = useNavigate();
  const { findUsedNodes } = useDialogNodesCache();
  const { updateIntentNameOnDialogs } = useDialogs(brainId);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const slug = useSelector(selectAccountSlug);

  const queryClient = useQueryClient();
  const {
    isNewIntentDraft,
    recommendedIntent,
    updateRecommendation,
    updateRecommendations,
    recommendedIntents,
    isRecommmedationsDirty,
  } = useIntentRecommendation(brainId, intentName);
  const { intentName: paramsIntent } = useParams();

  const intentDraft = useSelector(selectIntentDraft, shallowEqual);

  const isDraft = paramsIntent === 'draft';
  const sessionIntent = useSelector(selectIntentName);

  const { data: intents, status: listStatus } = useQuery<Intents, Error>({
    queryKey: [endpoints.intents(brainId)],
    queryFn: () => API.listIntents(brainId),
    enabled: !!brainId,
  });

  const { data: intent, isLoading } = useQuery<Intent, Error>({
    queryKey: [endpoints.intent(brainId, intentName)],
    queryFn: () => API.getIntent(brainId, intentName),
    enabled:
      !!brainId &&
      !!intentName &&
      !isDraft &&
      !!intents?.intents?.find((e) => intentName === e.intent),
    initialData: intents?.intents?.find((e) => intentName === e.intent),
  });

  const {
    mutate: updateIntent,
    mutateAsync: updateIntentAsync,
    status: updateStatus,
  } = useMutation<Intent, ApiError, UpdateIntentProps>({
    mutationFn: (data) => API.updateIntent(brainId, { ...data }),
    onSuccess: (resp, variables) => {
      if ('collection' in variables.intent) {
        intentCache.updated(queryClient, resp, { previousId: variables.name });
      } else {
        // When we rename an intent that has recommendation , rename recommendation too
        if (
          'new_intent' in variables.intent &&
          intentName !== variables.intent.new_intent &&
          recommendedIntent
        ) {
          const newIntent = variables.intent.new_intent;
          queryClient.setQueryData<Recommendation>(
            [recommendationsEndpoints.recommendations(resp.brain_id)],
            (prev: Recommendation) => {
              const newIntents = prev.intents.map((i) =>
                i.intent === intentName ? { ...i, intent: newIntent } : i
              );
              dispatch(
                setRecommendations({ brainId, recommendations: { ...prev, intents: newIntents } })
              );

              return { ...prev, intents: newIntents };
            }
          );
        }
      }

      const intentToUpdate = variables.name;

      intentCache.updated(queryClient, resp, { previousId: intentToUpdate });

      toast.success(t('intent.intent_saved', { 0: intentToUpdate }));
    },
    onError: (error) => {
      if (error.statusCode === 404) {
        toast.error(t('intent.intent_no_longer_exists'));
        intentCache.removed(queryClient, { parentId: brainId, id: intentName });
        navigate(`/${slug}/ai-agents/${brainId}/workflows/intents`);
        return;
      }
      toast.error(error.message);
    },
  });

  const { mutate: createIntent, status: createStatus } = useMutation({
    mutationFn: API.createIntent,
    onSuccess: (resp) => {
      dispatch(setIntent(resp));
      intentCache.created(queryClient, resp, { parentId: brainId });
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteIntent } = useMutation<
    Intent,
    ApiError,
    { brainId: string; intentName: string } | void
  >({
    mutationFn: (intent) => {
      // If the user specifies the intent, delete that one rather than
      // the one selected in the hook
      if (intent) {
        return API.deleteIntent(intent);
      }
      return API.deleteIntent({ brainId, intentName });
    },
    onSuccess: (resp, variables) => {
      dispatch(popModal());

      const variablesIntentName = variables && variables?.intentName;

      const intentToDelete = intentName ?? variablesIntentName ?? resp?.intent;

      const intentsInCollection = sameCollection('intents', 'intent', intentToDelete, intents);

      intentCache.removed(queryClient, { parentId: brainId, id: intentToDelete });
      toast.success(t('intent.intent_deleted', { 0: intentName ?? variablesIntentName }));
      dispatch(clearIntentExpressions());

      const url = `/${slug}/ai-agents/${brainId}/workflows/intents`;
      if (intentsInCollection.length === 0) {
        navigate(url);
      } else {
        navigate(`${url}/${intentsInCollection[0].intent}`);
      }
    },
    onError: (error, variables) => {
      if (error.statusCode === 404) {
        const intentToDelete = (variables && variables.intentName) ?? intentName;
        toast.error(t('intent.intent_no_longer_exists'));
        intentCache.removed(queryClient, { parentId: brainId, id: intentToDelete });
        navigate(`/${slug}/ai-agents/${brainId}/workflows/intents`);
        return;
      }
      toast.error(error.message);
    },
  });

  const createNewIntent = useCallback(
    async (newIntent?: Partial<Intent>) => {
      return new Promise<Intent>((resolve, reject): void => {
        createIntent(
          { brainId, newIntent },
          { onSuccess: (data) => resolve(data), onError: reject }
        );
      });
    },
    [brainId, createIntent]
  );

  const { mutate: updateCollection } = useMutation<Intents, Error, CollectionProps>({
    mutationFn: (data) => API.updateCollection(brainId, data),
    onSuccess: (resp) => {
      const newCollectionName = resp.intents[0].collection;
      queryClient.setQueryData<Intents>([endpoints.intents(brainId)], (prev: Intents) => ({
        intents: prev?.intents.map((item) => {
          const isUpdated = resp.intents.find((e) => e.intent === item.intent);
          return isUpdated ? { ...item, collection: newCollectionName } : item;
        }),
      }));
      toast.success(t('dialog.collection_updated', { 0: newCollectionName }));
    },
  });

  const saveIntent = useCallback(
    (shouldRedirect = true) => {
      // Save a draft intent
      if (isDraft) {
        const previousCollection = intents?.intents?.find(
          (e) => e.intent === sessionIntent
        )?.collection;

        const payload = {
          brainId,
          newIntent: {
            intent: intentDraft.new_intent,
            expressions: intentDraft.expressions,
            collection: previousCollection ?? '',
          },
        };

        return new Promise<Intent>((resolve, reject): void => {
          createIntent(payload, {
            onSuccess: (resp) => {
              toast.success(t('intent.intent_saved', { 0: resp?.intent }));
              if (shouldRedirect) {
                navigate(`/${slug}/ai-agents/${brainId}/workflows/intents/${resp?.intent}`);
              }

              resolve(resp);
            },
            onError: reject,
          });
        });
      }
      return new Promise<void>((resolve, reject): void => {
        //Save an intent that was created from a new recommendation
        if (isNewIntentDraft) {
          updateRecommendation();
          const payload = {
            brainId,
            newIntent: { intent: intentDraft?.new_intent, expressions: intentDraft?.expressions },
          };
          createIntent(payload, {
            onSuccess: (resp) => {
              dispatch(resetDirty({ brainId }));
              if (shouldRedirect) {
                navigate(`/${slug}/ai-agents/${brainId}/workflows/intents/${resp.intent}`);
              }

              resolve();
            },
            onError: (error) => reject(error),
          });
        } else {
          //Save an existing intent that had recommendations
          if (isRecommmedationsDirty) {
            updateRecommendations({ intents: recommendedIntents });
          }
          const isSystemIntent = SYSTEM_INTENTS.includes(intentDraft.new_intent);
          const updatePayload =
            paramsIntent === intentDraft.new_intent
              ? { name: intentName, intent: { expressions: intentDraft.expressions } }
              : {
                  name: paramsIntent ?? intentDraft.new_intent,
                  intent: {
                    new_intent: !isSystemIntent ? intentDraft.new_intent : undefined,
                    expressions: intentDraft.expressions,
                  },
                };

          updateIntent(updatePayload, {
            onSuccess: (resp) => {
              if (resp.intent !== intentName) {
                updateIntentNameOnDialogs(findUsedNodes({ brainId, name: intentName }), {
                  intent: resp.intent,
                });
                queryClient.removeQueries({ queryKey: [endpoints.intent(brainId, intentName)] });
                if (shouldRedirect) {
                  setTimeout(() => {
                    // This is a workaround to prevent "Unsaved changes" modal from showing up
                    navigate(`/${slug}/ai-agents/${brainId}/workflows/intents/${resp.intent}`);
                  }, 100);
                }
              }
              resolve();
            },
            onError: reject,
          });
        }
      });
    },
    [
      isDraft,
      intents?.intents,
      brainId,
      intentDraft.new_intent,
      intentDraft.expressions,
      sessionIntent,
      createIntent,
      dispatch,
      t,
      slug,
      isNewIntentDraft,
      updateRecommendation,
      isRecommmedationsDirty,
      paramsIntent,
      intentName,
      updateIntent,
      updateRecommendations,
      recommendedIntents,
      updateIntentNameOnDialogs,
      findUsedNodes,
      queryClient,
      navigate,
    ]
  );

  const handleSaveIntent = useCallback(
    (shouldRedirect?: boolean) => {
      const expressions = intentDraft?.expressions;
      if (expressions.length === 0) {
        setTimeout(() => {
          dispatch(
            pushModal(MODAL_CONFIRM_CHANGES, {
              onSave: () => saveIntent(shouldRedirect),
              onDiscard: () => {
                dispatch(popModal());
              },
              title: t('prompts.unsaved_changes.title'),
              subtitle: t('intent.empty'),
              secondaryText: t('common.cancel'),
              primaryText: t('common.save_anyway'),
            })
          );
        }, 0);
      } else {
        saveIntent(shouldRedirect);
      }
    },
    [dispatch, intentDraft?.expressions, saveIntent, t]
  );

  const createDraftIntent = useCallback(() => {
    navigate(`/${slug}/ai-agents/${brainId}/workflows/intents/draft`);
  }, [brainId, slug, navigate]);

  type CollectionUpdate = { id: string; collection: string };

  const handleCollectionUpdate = useCallback(
    ({ id, collection }: CollectionUpdate) => {
      updateIntent({ name: id, intent: { collection } });
    },
    [updateIntent]
  );

  const optimalPhrases = useMemo(() => getOptimalPhrases(intents?.intents), [intents?.intents]);

  const filteredSortedExpressions = useSelector(
    (state: RootState) => selectVisibleExpressions(state, { brainId, intentName, optimalPhrases }),
    shallowEqual
  );

  const intentNames = useMemo(
    () => intents?.intents?.map((i) => i.intent.toLowerCase()),
    [intents]
  );

  // Intents without offtopics
  const cleanIntents = useMemo(
    () => intents?.intents?.filter((i) => i.intent !== 'offtopics'),
    [intents]
  );

  const randomIntent = useMemo(() => getRandomElement(cleanIntents), [cleanIntents]);

  return {
    createNewIntent,
    createDraftIntent,
    deleteIntent,
    isLoading,
    createStatus,
    intent,
    intents: intents?.intents,
    cleanIntents,
    listStatus,
    filteredExpressions: filteredSortedExpressions,
    handleCollectionUpdate,
    optimalPhrases: getOptimalPhrases(intents?.intents),
    saveIntent,
    handleSaveIntent,
    updateCollection,
    updateIntent,
    updateIntentAsync,
    updateStatus,
    intentNames,
    randomIntent,
  };
};

export default useIntents;
